import React from 'react';
import { Helmet } from 'react-helmet-async';
import baseTheme from 'theme';

type MaybeString = string | false | null | undefined;

export type HeadProps = {
    title?: MaybeString;
    description?: MaybeString;
    font?: MaybeString;
    resetCss?: MaybeString;
    fullHeightCss?: MaybeString;
    customCss?: MaybeString;
    customScrollbars?: MaybeString;
};

const defaultResetCss = `
  *, *::before, *::after { box-sizing: border-box; }

  html {
    font-family: sans-serif;
    line-height: 1.15;
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0,0,0,0);
  }

  body {
    background-color: ${baseTheme.colors.body.background};
    margin: 0;
    height: inherit;
    color: ${baseTheme.colors.primaryText};
    font-family: ${baseTheme.fonts.sansSerif};
  }

  [tabindex="-1"]:focus { outline: 0 !important; }

  button, input, optgroup, select, textarea {
    font-family: inherit;
    font-size: inherit;
  }

  .rui-tooltip.rui-tooltip--standard {
    color: ${baseTheme.colors.tooltip.text};
    background-color: ${baseTheme.colors.tooltip.background};
    border-radius: ${baseTheme.borderRadius.small};
  }
  .rui-tooltip.rui-tooltip--standard.place-top:after { border-top-color: ${baseTheme.colors.tooltip.background}; }
  .rui-tooltip.rui-tooltip--standardplace-bottom:after { border-bottom-color: ${baseTheme.colors.tooltip.background}; }
  .rui-tooltip.rui-tooltip--standard.place-left:after { border-left-color: ${baseTheme.colors.tooltip.background}; }
  .rui-tooltip.rui-tooltip--standard.place-right:after { border-right-color: ${baseTheme.colors.tooltip.background}; }

  .rui-tooltip.rui-tooltip--inverted {
    color: ${baseTheme.colors.tooltip.inverted ? baseTheme.colors.tooltip.inverted.text : baseTheme.colors.tooltip.text};
    background-color: ${baseTheme.colors.tooltip.inverted ? baseTheme.colors.tooltip.inverted.background : baseTheme.colors.tooltip.background};
    border-radius: ${baseTheme.borderRadius.small};
  }
  .rui-tooltip.rui-tooltip--inverted.place-top:after {
    border-top-color: ${baseTheme.colors.tooltip.inverted ? baseTheme.colors.tooltip.inverted.background : baseTheme.colors.tooltip.background};
  }
  .rui-tooltip.rui-tooltip--inverted.place-bottom:after {
    border-bottom-color: ${baseTheme.colors.tooltip.inverted ? baseTheme.colors.tooltip.inverted.background : baseTheme.colors.tooltip.background};
  }
  .rui-tooltip.rui-tooltip--inverted.place-left:after {
    border-left-color: ${baseTheme.colors.tooltip.inverted ? baseTheme.colors.tooltip.inverted.background : baseTheme.colors.tooltip.background};
  }
  .rui-tooltip.rui-tooltip--inverted.place-right:after {
    border-right-color: ${baseTheme.colors.tooltip.inverted ? baseTheme.colors.tooltip.inverted.background : baseTheme.colors.tooltip.background};
  }
`;

const defaultFullHeightCss = `
  html { height: 100%; }
  body { height: 100%; }
  body > #root { height: 100%; }
`;

const defaultFontHref = 'https://fonts.googleapis.com/css?family=Open+Sans:300,300i,400,400i,600,700&display=swap';

export const Head: React.FC<HeadProps> = ({
    title,
    description,
    font = defaultFontHref,
    resetCss = defaultResetCss,
    fullHeightCss = defaultFullHeightCss,
    customCss = null,
}) => {
    return (
        <Helmet>
            {title && <title>{title}</title>}
            {description && <meta name="description" content={description} />}
            {font && <link href={font} rel="stylesheet" />}
            {resetCss && <style>{resetCss}</style>}
            {fullHeightCss && <style>{fullHeightCss}</style>}
            {customCss && <style>{customCss}</style>}
        </Helmet>
    );
};

Head.displayName = 'Head';
export default Head;
